import React, { useState, useEffect } from 'react';
import styled from '@emotion/styled';
import axios from 'axios';
import { toast } from 'react-toastify';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const Container = styled.div`
  width: min(100%, 900px);
  padding: clamp(14px, 4vw, 24px);
  margin: 0 auto;
  box-sizing: border-box;
`;

const Card = styled.div`
  background: white;
  border-radius: 12px;
  padding: clamp(20px, 4vw, 32px);
  box-shadow: 0 2px 8px rgba(0,0,0,0.08);
  margin-bottom: 24px;
  overflow: hidden;
`;

const Title = styled.h1`
  font-size: clamp(1.28rem, 6vw, 1.5rem);
  line-height: 1.2;
  margin: 0 0 8px;
  color: #1a1a2e;
`;

const Subtitle = styled.p`
  color: #666;
  margin: 0 0 clamp(22px, 5vw, 32px);
  line-height: 1.55;
`;

const FormGroup = styled.div`
  margin-bottom: 24px;
`;

const Label = styled.label`
  display: block;
  font-weight: 500;
  margin-bottom: 8px;
  color: #333;
`;

const Input = styled.input`
  width: 100%;
  padding: 12px 16px;
  border: 2px solid #e0e0e0;
  border-radius: 8px;
  font-size: 1rem;
  box-sizing: border-box;
  
  &:focus {
    outline: none;
    border-color: #1976d2;
  }
`;

const Button = styled.button`
  background: #1976d2;
  color: white;
  border: none;
  padding: 14px 28px;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  width: 100%;
  min-height: 48px;
  touch-action: manipulation;
  
  &:hover {
    background: #1565c0;
  }
  
  &:disabled {
    background: #ccc;
    cursor: not-allowed;
  }
`;

const SuccessBox = styled.div`
  background: #e8f5e9;
  border: 1px solid #a5d6a7;
  border-radius: 8px;
  padding: 24px;
  margin-top: 24px;
  
  h3 {
    color: #2e7d32;
    margin-bottom: 12px;
    margin-top: 0;
  }
  
  .link {
    background: white;
    padding: 12px;
    border-radius: 4px;
    word-break: break-all;
    margin: 12px 0;
    font-family: monospace;
    font-size: 0.9rem;
    border: 1px solid #c8e6c9;
  }
  
  .copy-btn {
    background: #2e7d32;
    color: white;
    border: none;
    padding: 10px 16px;
    border-radius: 4px;
    cursor: pointer;
    font-size: 0.9rem;
    min-height: 44px;
    touch-action: manipulation;
    
    &:hover {
      background: #1b5e20;
    }
  }
  
  .meta {
    margin-top: 12px;
    font-size: 0.875rem;
    color: #666;
  }
`;

const TestCard = styled.div`
  border: 2px solid ${props => props.selected ? '#1976d2' : '#e0e0e0'};
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 12px;
  cursor: pointer;
  transition: all 0.2s;
  background: ${props => props.selected ? '#e3f2fd' : 'white'};
  
  &:hover {
    border-color: #1976d2;
  }
  
  h4 {
    margin: 0 0 4px;
    color: #1a1a2e;
  }
  
  p {
    margin: 0;
    color: #666;
    font-size: 0.875rem;
  }
  
  .meta {
    display: flex;
    flex-wrap: wrap;
    gap: 8px 16px;
    margin-top: 8px;
    font-size: 0.8rem;
    color: #888;
  }
`;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px;

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const TableScroll = styled.div`
  width: 100%;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  border-radius: 8px;
`;

const CopyActionButton = styled.button`
  background: none;
  border: 1px solid #ddd;
  padding: 8px 10px;
  border-radius: 6px;
  cursor: pointer;
  font-size: 0.8rem;
  min-height: 40px;
  white-space: nowrap;
  touch-action: manipulation;
`;

const SectionTitle = styled.h2`
  font-size: 1.1rem;
  color: #1a1a2e;
  margin-bottom: 16px;
  margin-top: 0;
`;

const RecentAssignments = styled.div`
  margin-top: 32px;
`;

const Table = styled.table`
  width: 100%;
  min-width: 640px;
  border-collapse: collapse;
  
  th, td {
    padding: 12px;
    text-align: left;
    border-bottom: 1px solid #eee;
    font-size: 0.9rem;
  }
  
  th {
    background: #f9f9f9;
    font-weight: 600;
    color: #666;
  }
`;

const Badge = styled.span`
  padding: 4px 10px;
  border-radius: 20px;
  font-size: 0.75rem;
  font-weight: 600;
  background: ${props => {
    switch(props.status) {
      case 'completed': return '#e8f5e9';
      case 'started': return '#fff3e0';
      case 'pending': return '#e3f2fd';
      case 'expired': return '#ffebee';
      default: return '#f5f5f5';
    }
  }};
  color: ${props => {
    switch(props.status) {
      case 'completed': return '#2e7d32';
      case 'started': return '#f57c00';
      case 'pending': return '#1976d2';
      case 'expired': return '#c62828';
      default: return '#666';
    }
  }};
`;

const SendTest = () => {
  const [tests, setTests] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [selectedTest, setSelectedTest] = useState(null);
  const [clientName, setClientName] = useState('');
  const [clientEmail, setClientEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(true);
  const [success, setSuccess] = useState(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoadingData(true);
      const [testsRes, assignmentsRes] = await Promise.all([
        axios.get(`${API_URL}/admin/tests`),
        axios.get(`${API_URL}/admin/assignments`)
      ]);
      setTests(testsRes.data.filter(t => t.isActive));
      setAssignments(assignmentsRes.data.assignments || []);
    } catch (error) {
      console.error('Failed to load data:', error);
      toast.error('Failed to load tests');
    } finally {
      setLoadingData(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!selectedTest) {
      toast.error('Please select a test');
      return;
    }
    
    if (!clientName.trim()) {
      toast.error('Please enter client name');
      return;
    }
    
    if (!clientEmail.trim()) {
      toast.error('Please enter client email');
      return;
    }
    
    setLoading(true);
    
    try {
      const response = await axios.post(`${API_URL}/admin/assignments`, {
        testId: selectedTest,
        clientName: clientName.trim(),
        clientEmail: clientEmail.trim()
      });
      
      setSuccess(response.data.assignment);
      toast.success('Test link created successfully!');
      
      // Reset form
      setClientName('');
      setClientEmail('');
      setSelectedTest(null);
      
      // Reload assignments
      loadData();
    } catch (error) {
      console.error('Create assignment error:', error);
      toast.error(error.response?.data?.error || 'Failed to create test link');
    } finally {
      setLoading(false);
    }
  };

  const copyLink = (link) => {
    navigator.clipboard.writeText(link);
    toast.success('Link copied to clipboard!');
  };

  if (loadingData) {
    return <Container>Loading...</Container>;
  }

  return (
    <Container>
      <Card>
        <Title>📝 Send Assessment to Client</Title>
        <Subtitle>Create a unique test link to send directly to a client</Subtitle>
        
        <form onSubmit={handleSubmit}>
          <FormGroup>
            <SectionTitle>1. Select Assessment</SectionTitle>
            {tests.length > 0 ? (
              tests.map((test) => (
                <TestCard 
                  key={test.id}
                  selected={selectedTest === test.id}
                  onClick={() => setSelectedTest(test.id)}
                >
                  <h4>{test.name}</h4>
                  <p>{test.description || 'Assessment tool'}</p>
                  <div className="meta">
                    <span>⏱ {test.estimatedTime} min</span>
                    <span>💰 GHS {parseFloat(test.price).toFixed(2)}</span>
                    <span>📊 {test.shortName}</span>
                  </div>
                </TestCard>
              ))
            ) : (
              <p style={{ color: '#888' }}>No tests available</p>
            )}
          </FormGroup>
          
          <FormGroup>
            <SectionTitle>2. Client Information</SectionTitle>
            <FormGrid>
              <div>
                <Label>Client Name *</Label>
                <Input
                  type="text"
                  value={clientName}
                  onChange={(e) => setClientName(e.target.value)}
                  placeholder="Enter client's full name"
                  required
                />
              </div>
              <div>
                <Label>Client Email *</Label>
                <Input
                  type="email"
                  value={clientEmail}
                  onChange={(e) => setClientEmail(e.target.value)}
                  placeholder="client@email.com"
                  required
                />
              </div>
            </FormGrid>
          </FormGroup>
          
          <Button type="submit" disabled={loading || !selectedTest}>
            {loading ? 'Creating...' : '🔗 Generate Test Link'}
          </Button>
        </form>
        
        {success && (
          <SuccessBox>
            <h3>✅ Test Link Created!</h3>
            <p>Send this link to your client:</p>
            <div className="link">{success.testLink}</div>
            <button className="copy-btn" onClick={() => copyLink(success.testLink)}>
              📋 Copy Link
            </button>
            <div className="meta">
              <strong>Code:</strong> {success.uniqueCode} | 
              <strong> Expires:</strong> {new Date(success.expiresAt).toLocaleDateString()} |
              <strong> Client:</strong> {success.clientName}
            </div>
          </SuccessBox>
        )}
      </Card>
      
      {assignments.length > 0 && (
        <Card>
          <RecentAssignments>
            <SectionTitle>📋 Recent Test Links</SectionTitle>
            <TableScroll>
              <Table>
              <thead>
                <tr>
                  <th>Client</th>
                  <th>Test</th>
                  <th>Code</th>
                  <th>Status</th>
                  <th>Created</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {assignments.slice(0, 10).map(a => (
                  <tr key={a.id}>
                    <td>
                      <strong>{a.clientName}</strong>
                      <div style={{ fontSize: '0.8rem', color: '#888' }}>{a.clientEmail}</div>
                    </td>
                    <td>{a.TestCatalog?.shortName}</td>
                    <td><code>{a.uniqueCode}</code></td>
                    <td><Badge status={a.status}>{a.status}</Badge></td>
                    <td>{new Date(a.createdAt).toLocaleDateString()}</td>
                    <td>
                      <CopyActionButton
                        onClick={() => copyLink(`${process.env.REACT_APP_CLIENT_URL || 'http://localhost:3003'}/test/${a.uniqueCode}`)}
                      >
                        📋 Copy
                      </CopyActionButton>
                    </td>
                  </tr>
                ))}
              </tbody>
              </Table>
            </TableScroll>
          </RecentAssignments>
        </Card>
      )}
    </Container>
  );
};

export default SendTest;